import React, { useState } from 'react';
import {
  ConfigProvider,
  Select,
  DatePicker,
  Tooltip,
  Spin,
} from 'antd';
import {
  LineChartOutlined,
  BarChartOutlined,
  MobileOutlined,
  TeamOutlined,
  CalendarOutlined,
  ArrowLeftOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
  EyeOutlined,
  QrcodeOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import 'antd/dist/reset.css';
import './AnalyticsPage.css';
import { useAnalytics } from '../../hooks/useAnalytics';

const { RangePicker } = DatePicker;

/* ── Date Ranges ──────────────────────────────────── */
const DATE_RANGES = [
  { value: 'today',         label: 'Today' },
  { value: 'yesterday',     label: 'Yesterday' },
  { value: 'last_7_days',   label: 'Last 7 Days' },
  { value: 'last_14_days',  label: 'Last 14 Days' },
  { value: 'last_30_days',  label: 'Last 30 Days' },
  { value: 'this_week',     label: 'This Week' },
  { value: 'last_week',     label: 'Last Week' },
  { value: 'this_month',    label: 'This Month' },
  { value: 'last_month',    label: 'Last Month' },
  { value: 'last_3_months', label: 'Last 3 Months' },
  { value: 'last_6_months', label: 'Last 6 Months' },
  { value: 'last_9_months', label: 'Last 9 Months' },
  { value: 'last_12_months',label: 'Last 12 Months' },
  { value: 'this_quarter',  label: 'This Quarter' },
  { value: 'last_quarter',  label: 'Last Quarter' },
  { value: 'this_year',     label: 'This Year' },
  { value: 'last_year',     label: 'Last Year' },
  { value: 'all_time',      label: 'All Time' },
  { value: 'custom',        label: 'Custom Range' },
];

const DATE_FORMAT = 'YYYY-MM-DD';

/* Color palette for charts */
const CHART_COLORS = ['#3b82f6', '#f59e0b', '#22c55e', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];

/* ── Mock Data ──────────────────────────────────────── */
const MOCK_SUMMARY = {
  totalScans: 12854,
  totalScansChange: 18.5,
  qrCodesCreated: 234,
  qrCodesChange: 12.3,
  uniqueVisitors: 8932,
  visitorsChange: -5.2,
  avgScanTime: '4.2s',
};

const MOCK_SCANS_OVER_TIME = [
  { date: 'Mar 06', scans: 850 },
  { date: 'Mar 07', scans: 920 },
  { date: 'Mar 08', scans: 1200 },
  { date: 'Mar 09', scans: 1080 },
  { date: 'Mar 10', scans: 1450 },
  { date: 'Mar 11', scans: 1650 },
  { date: 'Mar 12', scans: 1920 },
];

const MOCK_TOP_QR_CODES = [
  { key: 1, qrName: 'Spring Campaign 2026', scans: 3420, created: '2026-02-15', creator: 'John Doe' },
  { key: 2, qrName: 'Product Launch Event', scans: 2850, created: '2026-02-20', creator: 'Sarah Smith' },
  { key: 3, qrName: 'Promo Code - Newsletter', scans: 2340, created: '2026-03-01', creator: 'Mike Johnson' },
  { key: 4, qrName: 'WiFi Access', scans: 1920, created: '2026-01-10', creator: 'Emma Wilson' },
  { key: 5, qrName: 'Event Registration', scans: 1580, created: '2026-03-05', creator: 'Alex Brown' },
  { key: 6, qrName: 'Contact Card', scans: 1240, created: '2026-02-28', creator: 'Lisa Park' },
];

const MOCK_DEVICE_DATA = [
  { key: 1, label: 'Mobile', scans: 8240, color: '#3b82f6' },
  { key: 2, label: 'Desktop', scans: 3720, color: '#f59e0b' },
  { key: 3, label: 'Tablet', scans: 894, color: '#22c55e' },
];

const MOCK_BROWSER_DATA = [
  { key: 1, label: 'Chrome', scans: 6680, color: '#3b82f6' },
  { key: 2, label: 'Safari', scans: 3564, color: '#8b5cf6' },
  { key: 3, label: 'Firefox', scans: 1240, color: '#f59e0b' },
  { key: 4, label: 'Samsung', scans: 890, color: '#22c55e' },
  { key: 5, label: 'Edge', scans: 480, color: '#ef4444' },
];

const MOCK_QR_CREATORS = [
  { key: 1, creator: 'John Doe', qrCount: 28, totalScans: 3420 },
  { key: 2, creator: 'Sarah Smith', qrCount: 15, totalScans: 2850 },
  { key: 3, creator: 'Mike Johnson', qrCount: 31, totalScans: 2340 },
  { key: 4, creator: 'Emma Wilson', qrCount: 9, totalScans: 1920 },
  { key: 5, creator: 'Alex Brown', qrCount: 22, totalScans: 1580 },
];

const MOCK_PEAK_HOURS = [
  { hour: '6am', scans: 120 },
  { hour: '8am', scans: 580 },
  { hour: '10am', scans: 920 },
  { hour: '12pm', scans: 1340 },
  { hour: '2pm', scans: 1180 },
  { hour: '4pm', scans: 890 },
  { hour: '6pm', scans: 1050 },
  { hour: '8pm', scans: 760 },
  { hour: '10pm', scans: 420 },
];

/* ═══════════════════════════════════════════════════════
   CHART COMPONENTS (pure CSS — no chart library needed)
   ═══════════════════════════════════════════════════════ */

/* ── Card spinner ── */
const CardLoader = () => (
  <div className="card-spinner">
    <Spin size="default" />
  </div>
);

/* ── Line Chart with area fill ── */
const LineChart = ({ data, valueKey, labelKey }) => {
  if (!data || data.length < 2) {
    return (
      <svg viewBox="0 0 600 220" className="analytics-svg-chart" preserveAspectRatio="xMidYMid meet">
        <text x="300" y="115" textAnchor="middle" className="analytics-svg-tick">No data available</text>
      </svg>
    );
  }

  const max = Math.max(...data.map((d) => d[valueKey]));
  const min = Math.min(...data.map((d) => d[valueKey]));
  const range = max - min || 1;
  const w = 600;
  const h = 220;
  const padX = 50;
  const padY = 30;
  const chartW = w - padX * 2;
  const chartH = h - padY * 2;

  const points = data.map((d, i) => ({
    x: padX + (i / (data.length - 1)) * chartW,
    y: padY + chartH - ((d[valueKey] - min) / range) * chartH,
    val: d[valueKey],
    label: d[labelKey],
  }));

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  const areaPath = `${linePath} L ${points[points.length - 1].x} ${padY + chartH} L ${points[0].x} ${padY + chartH} Z`;

  // Y-axis ticks
  const yTicks = 5;
  const yLines = Array.from({ length: yTicks + 1 }, (_, i) => {
    const val = min + (range / yTicks) * i;
    const y = padY + chartH - (chartH / yTicks) * i;
    return { val: Math.round(val), y };
  });

  // Density tiers based on number of data points
  const dense  = data.length > 90;  // year+
  const medium = data.length > 45;  // quarter

  // How many x-axis / value labels to show (at most ~10)
  const maxLabels = dense ? 8 : medium ? 10 : 10;
  const step = Math.max(1, Math.ceil(data.length / maxLabels));
  const showXLabel  = (i) => i % step === 0 || i === data.length - 1;
  // For dense data, value-on-dot labels are skipped entirely (too cluttered)
  const showValLabel = (i) => !dense && (i % step === 0 || i === data.length - 1);

  const dotR = dense ? 0 : medium ? 2.5 : 4;   // hide dots for year data
  const sw   = dense ? 0 : medium ? 1   : 1.5;
  const lineW = dense ? 1.5 : medium ? 2 : 2.5;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="analytics-svg-chart" preserveAspectRatio="xMidYMid meet">
      {/* Grid lines */}
      {yLines.map((t, i) => (
        <g key={i}>
          <line x1={padX} y1={t.y} x2={w - padX} y2={t.y} stroke="var(--border-color)" strokeWidth="1" />
          <text x={padX - 8} y={t.y + 4} textAnchor="end" className="analytics-svg-tick">{t.val}</text>
        </g>
      ))}
      {/* Area fill */}
      <defs>
        <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill="url(#areaGrad)" />
      {/* Line */}
      <path d={linePath} fill="none" stroke="#3b82f6" strokeWidth={lineW} strokeLinecap="round" strokeLinejoin="round" />
      {/* Points & labels — dots hidden for dense (year) data, only x-labels shown */}
      {points.map((p, i) => (
        <g key={i}>
          {dotR > 0 && (
            <circle cx={p.x} cy={p.y} r={dotR} fill="#3b82f6" stroke="#fff" strokeWidth={sw} className="analytics-svg-point" />
          )}
          {showValLabel(i) && (
            <text x={p.x} y={p.y - 9} textAnchor="middle" className="analytics-svg-value">{p.val.toLocaleString()}</text>
          )}
          {showXLabel(i) && (
            <text x={p.x} y={padY + chartH + 18} textAnchor="middle" className="analytics-svg-label">{p.label}</text>
          )}
        </g>
      ))}
    </svg>
  );
};

/* ── Horizontal bar chart ── */
const HBarChart = ({ data, nameKey, valueKey, maxVal }) => {
  if (!data || data.length === 0) return <div className="chart-empty">No data available</div>;
  const top = maxVal || Math.max(...data.map((d) => d[valueKey])) || 1;
  return (
    <div className="hbar-chart">
      {data.map((item, i) => {
        const pct = (item[valueKey] / top) * 100;
        return (
          <div key={item.key || i} className="hbar-row">
            <span className="hbar-rank">#{i + 1}</span>
            <div className="hbar-info">
              <div className="hbar-name-row">
                <span className="hbar-name">{item[nameKey]}</span>
                <span className="hbar-val">{item[valueKey].toLocaleString()}</span>
              </div>
              <div className="hbar-track">
                <div
                  className="hbar-fill"
                  style={{
                    width: `${pct}%`,
                    background: CHART_COLORS[i % CHART_COLORS.length],
                    animationDelay: `${i * 0.08}s`,
                  }}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ── Donut chart ── */
const DonutChart = ({ data, labelKey, valueKey }) => {
  const total = data.reduce((s, d) => s + d[valueKey], 0);
  const size = 180;
  const cx = size / 2;
  const cy = size / 2;
  const r = 70;
  const stroke = 22;

  let cumAngle = -90;
  const slices = data.map((d) => {
    const pct = d[valueKey] / total;
    const angle = pct * 360;
    const start = cumAngle;
    cumAngle += angle;
    return { ...d, pct, startAngle: start, endAngle: start + angle };
  });

  const polarToXY = (angle, radius) => ({
    x: cx + radius * Math.cos((angle * Math.PI) / 180),
    y: cy + radius * Math.sin((angle * Math.PI) / 180),
  });

  return (
    <div className="donut-wrapper">
      <svg viewBox={`0 0 ${size} ${size}`} className="donut-svg">
        {slices.map((s, i) => {
          const start = polarToXY(s.startAngle, r);
          const end = polarToXY(s.endAngle, r);
          const large = s.endAngle - s.startAngle > 180 ? 1 : 0;
          const d = `M ${start.x} ${start.y} A ${r} ${r} 0 ${large} 1 ${end.x} ${end.y}`;
          return (
            <path
              key={i}
              d={d}
              fill="none"
              stroke={s.color || CHART_COLORS[i]}
              strokeWidth={stroke}
              strokeLinecap="butt"
              className="donut-slice"
              style={{ animationDelay: `${i * 0.12}s` }}
            />
          );
        })}
        <text x={cx} y={cy - 6} textAnchor="middle" className="donut-total-val">{total.toLocaleString()}</text>
        <text x={cx} y={cy + 12} textAnchor="middle" className="donut-total-label">total</text>
      </svg>
      <div className="donut-legend">
        {slices.map((s, i) => (
          <div key={i} className="donut-legend-item">
            <span className="donut-dot" style={{ background: s.color || CHART_COLORS[i] }} />
            <span className="donut-legend-label">{s[labelKey]}</span>
            <span className="donut-legend-val">{s[valueKey].toLocaleString()}</span>
            <span className="donut-legend-pct">{(s.pct * 100).toFixed(1)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ── Grouped bar chart (two metrics side by side) ── */
const GroupedBarChart = ({ data, labelKey, metrics }) => {
  if (!data || data.length === 0) return <div className="chart-empty">No data available</div>;
  const maxVal = Math.max(...data.flatMap((d) => metrics.map((m) => d[m.key]))) || 1;
  return (
    <div className="grouped-chart">
      <div className="grouped-legend">
        {metrics.map((m, i) => (
          <span key={i} className="grouped-legend-item">
            <span className="grouped-legend-dot" style={{ background: m.color }} />
            {m.label}
          </span>
        ))}
      </div>
      <div className="grouped-bars-wrap">
        {data.map((item, idx) => (
          <div key={idx} className="grouped-col">
            <div className="grouped-bars">
              {metrics.map((m, mi) => {
                const h = (item[m.key] / maxVal) * 100;
                return (
                  <Tooltip key={mi} title={`${m.label}: ${item[m.key].toLocaleString()}`}>
                    <div className="grouped-bar-wrap">
                      <span className="grouped-bar-tip">{item[m.key].toLocaleString()}</span>
                      <div
                        className="grouped-bar"
                        style={{
                          height: `${h}%`,
                          background: m.color,
                          animationDelay: `${idx * 0.08 + mi * 0.04}s`,
                        }}
                      />
                    </div>
                  </Tooltip>
                );
              })}
            </div>
            <span className="grouped-label">{item[labelKey]}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════════ */
const AnalyticsPage = () => {
  const navigate = useNavigate();

  const [dateRange, setDateRange] = useState('last_30_days');
  const [customRange, setCustomRange] = useState([null, null]);

  const isCustom = dateRange === 'custom';

  /* Build the range param sent to the API */
  const apiRange = isCustom && customRange[0] && customRange[1]
    ? `custom:${customRange[0].format(DATE_FORMAT)}:${customRange[1].format(DATE_FORMAT)}`
    : dateRange;

  const {
    loading, error, refetch,
    loadingScans,
    loadingTopQr,
    loadingDeviceBrowser,
    loadingCreators,
    scansOverTime = [],
    topQrCodes    = [],
    devices       = [],
    browsers      = [],
    qrCreators    = [],
    summaryStats  = {},
  } = useAnalytics(apiRange);

  const dateLabel = isCustom && customRange[0] && customRange[1]
    ? `${customRange[0].format(DATE_FORMAT)} → ${customRange[1].format(DATE_FORMAT)}`
    : DATE_RANGES.find((d) => d.value === dateRange)?.label;

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#3b82f6',
          borderRadius: 8,
          fontFamily: "'Gabarito', sans-serif",
        },
      }}
    >
      <div className="analytics-root">

        {/* ── Header ── */}
        <div className="analytics-header">
          <div className="analytics-header-left">
            <button className="analytics-back-btn" onClick={() => navigate('/generate')}>
              <ArrowLeftOutlined />
            </button>
            <div className="analytics-header-dot" />
            <h1 className="analytics-header-title">Analytics</h1>
          </div>
          <button className="analytics-refetch-btn" onClick={refetch} disabled={loading}>
            <ReloadOutlined spin={loading} />
          </button>
        </div>

        {/* ── Body ── */}
        <div className="analytics-body">

          {/* ── Summary Stats ── */}
          <div className="analytics-summary">
            <div className="stat-cards-row">

              <div className="analytics-stat-card analytics-stat-card--blue">
                <div className="stat-card-icon-box stat-card-icon-box--blue">
                  <ThunderboltOutlined />
                </div>
                <div className="stat-card-text">
                  <span className="analytics-stat-label">Total Scans</span>
                  <span className="analytics-stat-value">
                    {loading ? '—' : (summaryStats.totalScans || 0).toLocaleString()}
                  </span>
                </div>
              </div>

              <div className="analytics-stat-card analytics-stat-card--amber">
                <div className="stat-card-icon-box stat-card-icon-box--amber">
                  <EyeOutlined />
                </div>
                <div className="stat-card-text">
                  <span className="analytics-stat-label">Total Visits</span>
                  <span className="analytics-stat-value">
                    {loading ? '—' : (summaryStats.totalVisits || 0).toLocaleString()}
                  </span>
                </div>
              </div>

              <div className="analytics-stat-card analytics-stat-card--green">
                <div className="stat-card-icon-box stat-card-icon-box--green">
                  <QrcodeOutlined />
                </div>
                <div className="stat-card-text">
                  <span className="analytics-stat-label">Active QR Codes</span>
                  <span className="analytics-stat-value">
                    {loading ? '—' : summaryStats.qrCodesActive || 0}
                  </span>
                </div>
              </div>

              <div className="analytics-stat-card analytics-stat-card--purple">
                <div className="stat-card-icon-box stat-card-icon-box--purple">
                  <TrophyOutlined />
                </div>
                <div className="stat-card-text">
                  <span className="analytics-stat-label">Top Code Scans</span>
                  <span className="analytics-stat-value">
                    {loading ? '—' : (topQrCodes[0]?.scans || 0).toLocaleString()}
                  </span>
                </div>
              </div>

            </div>
          </div>

          {/* ── Filters ── */}
          <div className="analytics-filters">
            <div className="analytics-filter-group">
              <label className="analytics-filter-label">Date Range</label>
              <Select
                className="analytics-select"
                value={dateRange}
                onChange={setDateRange}
                options={DATE_RANGES}
                suffixIcon={<CalendarOutlined />}
                popupMatchSelectWidth={200}
              />
            </div>

            {isCustom && (
              <div className="analytics-filter-group analytics-filter-group--dates">
                <label className="analytics-filter-label">Start / End</label>
                <RangePicker
                  className="analytics-range-picker"
                  value={customRange}
                  onChange={(dates) => setCustomRange(dates || [null, null])}
                  format={DATE_FORMAT}
                  allowClear
                />
              </div>
            )}

            <span className="analytics-date-badge">{dateLabel}</span>
          </div>

          {/* ── Reports Dashboard Grid (3×2) ── */}
          {error && (
            <div className="analytics-error">
              Failed to load analytics data. <button onClick={refetch}>Retry</button>
            </div>
          )}

          <div className="analytics-dashboard-grid">

            {/* ─ 1. Scans Over Time ─ */}
            <div className="dashboard-card">
              <div className="dashboard-card-header">
                <span className="dashboard-card-icon"><LineChartOutlined /></span>
                <span className="dashboard-card-title">Scans Over Time</span>
              </div>
              <div className="dashboard-card-body">
                {loadingScans ? <CardLoader /> : (
                  <div className="report-chart-container">
                    <LineChart data={scansOverTime} valueKey="scans" labelKey="date" />
                  </div>
                )}
              </div>
            </div>

            {/* ─ 2. Top QR Codes ─ */}
            <div className="dashboard-card">
              <div className="dashboard-card-header">
                <span className="dashboard-card-icon"><BarChartOutlined /></span>
                <span className="dashboard-card-title">Top QR Codes</span>
              </div>
              <div className="dashboard-card-body">
                {loadingTopQr ? <CardLoader /> : <HBarChart data={topQrCodes} nameKey="qrName" valueKey="scans" />}
              </div>
            </div>

            {/* ─ 3. Device Breakdown ─ */}
            <div className="dashboard-card">
              <div className="dashboard-card-header">
                <span className="dashboard-card-icon"><MobileOutlined /></span>
                <span className="dashboard-card-title">Device Breakdown</span>
              </div>
              <div className="dashboard-card-body">
                {loadingDeviceBrowser ? <CardLoader /> : <HBarChart data={devices} nameKey="label" valueKey="scans" />}
              </div>
            </div>

            {/* ─ 4. Browser Breakdown ─ */}
            <div className="dashboard-card">
              <div className="dashboard-card-header">
                <span className="dashboard-card-icon"><BarChartOutlined /></span>
                <span className="dashboard-card-title">Browser Breakdown</span>
              </div>
              <div className="dashboard-card-body">
                {loadingDeviceBrowser ? <CardLoader /> : <HBarChart data={browsers} nameKey="label" valueKey="scans" />}
              </div>
            </div>

            {/* ─ 5. QR Code Creators ─ */}
            <div className="dashboard-card">
              <div className="dashboard-card-header">
                <span className="dashboard-card-icon"><TeamOutlined /></span>
                <span className="dashboard-card-title">QR Code Creators</span>
              </div>
              <div className="dashboard-card-body">
                {loadingCreators ? <CardLoader /> : (
                  <GroupedBarChart
                    data={qrCreators}
                    labelKey="creator"
                    metrics={[
                      { key: 'topScans',     label: 'Top QR Scans',  color: '#3b82f6' },
                      { key: 'codesCreated', label: 'Codes Created', color: '#f59e0b' },
                    ]}
                  />
                )}
              </div>
            </div>

          </div>

        </div>
      </div>
    </ConfigProvider>
  );
};

export default AnalyticsPage;
